import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Calendar,
  Clock,
  DollarSign,
  Trash2,
  Edit,
  CreditCard,
  ArrowLeft,
  AlertCircle,
  MessageCircle,
  CheckCircle2,
  MapPin,
  FileText,
  Camera,
  X,
  Loader
} from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import apiService from '@/services/api';
import WorkProgressTimeline, { resolveTimelineStatus } from '@/components/WorkProgressTimeline';
import RescheduleModal from '@/components/RescheduleModal';
import { calculatePrice, CalculationResult, calculatePriceDifference } from '@/utils/pricingCalculator';

interface Quote {
  id: number;
  customer_id: number;
  service_id: number;
  service_name: string;
  service_type?: string;
  category_id: number;
  category_name: string;
  property_type: string;
  postcode: string;
  customer_name?: string;
  customer_email?: string;
  customer_phone?: string;
  customer_location?: string;
  preferred_service_date?: string;
  preferred_service_time?: string;
  total_amount_pence: number;
  total_amount: number;
  pricing_snapshot?: any;
  approval_status: string;
  payment_status?: string;
  is_accepted: number;
  created_at: string;
  updated_at: string;
}

interface Notification {
  id: number;
  user_id: number;
  type: string;
  title: string;
  message: string;
  read_at?: string;
  created_at: string;
  metadata?: any;
}

const QuoteDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();

  const [quote, setQuote] = useState<Quote | null>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Dynamic price recalculation state
  const [recalculatedPrice, setRecalculatedPrice] = useState<CalculationResult | null>(null);
  const [priceDifference, setPriceDifference] = useState<any>(null);
  const [isRecalculating, setIsRecalculating] = useState(false);

  // Fetch quote details on initial load or when user manually refreshes
  useEffect(() => {
    const fetchQuoteDetails = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const response = await apiService.request(`/quotes/${id}`, 'GET');
        if (response.success && response.data) {
          setQuote(response.data);
          console.log('✅ Quote fetched:', response.data.approval_status, response.data.payment_status);
          setError(null); // 🟢 Clear any previous errors
        } else {
          setError('Quote not found');
        }
      } catch (err) {
        console.error('Failed to fetch quote:', err);
        setError('Failed to load quote details');
      } finally {
        setLoading(false);
      }
    };

    // 🟢 Fetch only on initial load, not continuously
    fetchQuoteDetails();
    
    // No polling - user must manually refresh to get updates
  }, [id]);

  // Handle payment success - verify payment when redirected from Stripe
  useEffect(() => {
    const handlePaymentSuccess = async () => {
      if (!id) return;
      
      // Check if payment=success parameter is in URL
      const searchParams = new URLSearchParams(window.location.search);
      const paymentStatus = searchParams.get('payment');
      
      if (paymentStatus === 'success') {
        console.log('🔍 Payment success detected, verifying payment with backend...');
        
        try {
          // Get session ID from sessionStorage
          const sessionId = sessionStorage.getItem(`stripe_session_${id}`);
          
          if (!sessionId) {
            console.warn('⚠️ No session ID found in sessionStorage');
            return;
          }
          
          // Call verification endpoint to confirm payment
          const response = await apiService.request('/quotes/payment/verify-session', 'POST', {
            quoteId: parseInt(id),
            sessionId: sessionId
          });
          
          if (response.success && response.completed) {
            console.log('✅ Payment verified successfully!');
            
            // Refresh quote data to get updated payment status
            const quoteResponse = await apiService.request(`/quotes/${id}`, 'GET');
            if (quoteResponse.success && quoteResponse.data) {
              const updatedQuote = quoteResponse.data;
              setQuote(updatedQuote);
              
              // Verify payment amount matches quote amount
              console.log('💰 Payment Verification:', {
                quoteId: updatedQuote.id,
                quoteAmount: `$${(updatedQuote.total_amount_pence / 100).toFixed(2)}`,
                paymentStatus: updatedQuote.payment_status,
                approvalStatus: updatedQuote.approval_status,
                isPaymentComplete: updatedQuote.payment_status === 'completed'
              });
            }
            
            toast({
              title: 'Payment Confirmed',
              description: 'Your payment has been processed successfully.',
            });
            
            // Remove the payment=success parameter from URL
            const newUrl = `${window.location.pathname}?${new URLSearchParams(
              Array.from(searchParams.entries()).filter(([key]) => key !== 'payment')
            ).toString()}`;
            window.history.replaceState({}, '', newUrl);
          } else {
            console.log('⚠️ Payment verification incomplete, will retry via polling');
          }
        } catch (error) {
          console.error('Payment verification error:', error);
          // Continue with polling - it will catch the update eventually
        }
      }
    };
    
    // Only run once on component mount
    handlePaymentSuccess();
  }, [id, toast]);

  // Fetch notifications for this quote
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!id) return;
      try {
        // Fetch all notifications for user
        const response = await apiService.request(`/notifications`, 'GET');
        if (response.success && Array.isArray(response.data)) {
          // Filter to only show notifications related to this quote
          // Notifications contain quote info in title or message
          const quoteNotifications = response.data.filter((notif: Notification) => 
            notif.title?.includes(`#${id}`) || 
            notif.message?.includes(`#${id}`) ||
            (notif.metadata && typeof notif.metadata === 'object' && (notif.metadata as any).quote_id === parseInt(id))
          );
          setNotifications(quoteNotifications);
        }
      } catch (err) {
        console.debug('Failed to fetch notifications:', err);
      }
    };

    fetchNotifications();
    const interval = setInterval(fetchNotifications, 5000); // Poll every 5 seconds
    return () => clearInterval(interval);
  }, [id]);

  // Recalculate price dynamically based on current database values (admin changes)
  // ONLY for pending/quoted quotes - finalized quotes have fixed prices
  useEffect(() => {
    const recalculatePrice = async () => {
      // Only recalculate for pending/quoted quotes that are still under review
      // Do NOT recalculate for: approved, provider_assigned, confirmed, completed, rejected
      const finalizedStatuses = ['customer_approved', 'admin_approved', 'provider_assigned', 'booking_confirmed', 'completed', 'rejected', 'approved'];
      if (!quote || !quote.service_id || finalizedStatuses.includes(quote.approval_status?.toLowerCase() || '')) {
        return;
      }
      
      setIsRecalculating(true);
      try {
        console.log('[QuoteDetailsPage] 🔄 Recalculating price from current database values...');
        
        // Find actual base price from service using public endpoint
        const serviceResponse = await apiService.request(`/services`, 'GET');
        let basePricePence = 5000; // Default fallback
        
        if (serviceResponse.success && Array.isArray(serviceResponse.data)) {
          const service = serviceResponse.data.find((s: any) => s.id === quote.service_id);
          if (service) {
            basePricePence = service.base_price_pence || 5000;
          }
        }
        
        // Use shared pricing calculator with current quote parameters
        const result = await calculatePrice({
          service_id: quote.service_id,
          service_name: quote.service_name,
          postcode: quote.postcode || 'UK',
          property_type: quote.property_type || 'residential',
          urgency: 'standard', // Use standard urgency for recalc
          base_price_pence: basePricePence,
          quantity: 1,
        });
        
        if (result) {
          setRecalculatedPrice(result);
          
          // Calculate difference between original stored price and recalculated price
          const difference = calculatePriceDifference(
            quote.total_amount_pence,
            result.grand_total_pence
          );
          setPriceDifference(difference);
          
          console.log('[QuoteDetailsPage] ✅ Price recalculated:', {
            original: (quote.total_amount_pence / 100).toFixed(2),
            current: result.grand_total_gbp,
            difference: difference.formattedDifference,
            isIncrease: difference.isIncrease,
          });
        }
      } catch (error) {
        console.error('[QuoteDetailsPage] ❌ Error recalculating price:', error);
      } finally {
        setIsRecalculating(false);
      }
    };
    
    recalculatePrice();
  }, [quote?.service_id, quote?.id, quote?.approval_status]);

  // Helper to determine if quote is finalized (no price recalc needed)
  const isQuoteFinalized = quote && ['customer_approved', 'admin_approved', 'provider_assigned', 'booking_confirmed', 'completed', 'rejected', 'approved'].includes(quote.approval_status?.toLowerCase() || '');

  const handleDeleteQuote = async () => {
    if (!id) return;
    setIsDeleting(true);
    try {
      const response = await apiService.request(`/quotes/${id}`, 'DELETE');
      if (response.success) {
        toast({
          title: 'Quote Deleted',
          description: 'Quote has been deleted successfully',
        });
        navigate('/dashboard');
      } else {
        toast({
          title: 'Delete Failed',
          description: response.message || 'Could not delete quote',
          variant: 'destructive',
        });
      }
    } catch (err) {
      console.error('Delete error:', err);
      toast({
        title: 'Error',
        description: 'Failed to delete quote',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
      setDeleteConfirmOpen(false);
    }
  };

  const handlePayNow = async () => {
    if (!quote?.id) return;
    try {
      // Redirect to payment page
      window.location.href = `/dashboard/quotes/${quote.id}/payment`;
    } catch (err) {
      console.error('Payment error:', err);
      toast({
        title: 'Error',
        description: 'Could not proceed to payment',
        variant: 'destructive',
      });
    }
  };

  // Resolve timeline status from backend approval_status + payment_status
  const timelineStatus = resolveTimelineStatus(
    quote?.approval_status || 'submitted',
    quote?.payment_status
  );

  // 🟢 Show error only if we have no quote AND there's an error
  if (!quote && (error || loading)) {
    return (
      <>
        <DashboardLayout>
          <section className="section-padding bg-gradient-to-br from-slate-50 to-slate-100 min-h-screen">
          <div className="container-custom flex items-center justify-center min-h-[60vh]">
            {loading ? (
              <Card className="max-w-md w-full shadow-xl">
                <CardContent className="pt-12 pb-12 text-center">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-primary mb-6"></div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading Quote Details</h2>
                  <p className="text-gray-600">Please wait while we retrieve your quote information...</p>
                </CardContent>
              </Card>
            ) : (
              <Card className="max-w-md w-full shadow-xl">
                <CardContent className="pt-8 pb-8">
                  <div className="text-center">
                    <AlertCircle className="h-14 w-14 text-red-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-gray-900 mb-3">Quote Not Found</h2>
                    <p className="text-gray-600 mb-6">{error || 'The quote you are looking for does not exist or has been deleted.'}</p>
                    <Button onClick={() => navigate('/dashboard')} className="w-full h-10 font-semibold">
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Return to Dashboard
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </section>
        </DashboardLayout>
      </>
    );
  }

  // 🟢 If quote exists, show it even if still loading (smooth rendering)
  if (!quote) {
    return (
      <>
      <DashboardLayout>
        <section className="section-padding bg-gradient-to-br from-slate-50 to-slate-100 min-h-screen">
          <div className="container-custom flex items-center justify-center min-h-[60vh]">
            <Card className="max-w-md w-full shadow-xl">
              <CardContent className="pt-8 pb-8">
                <div className="text-center">
                  <AlertCircle className="h-14 w-14 text-red-500 mx-auto mb-4" />
                  <h2 className="text-2xl font-bold text-gray-900 mb-3">Quote Not Found</h2>
                  <p className="text-gray-600 mb-6">{error || 'The quote you are looking for does not exist or has been deleted.'}</p>
                  <Button onClick={() => navigate('/dashboard')} className="w-full h-10 font-semibold">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Return to Dashboard
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
        </DashboardLayout>
      </>
    );
  }
  const amountGBP = quote.total_amount_pence ? (quote.total_amount_pence / 100).toFixed(2) : '0.00';
  const pricingSnapshot = quote.pricing_snapshot ? JSON.parse(typeof quote.pricing_snapshot === 'string' ? quote.pricing_snapshot : JSON.stringify(quote.pricing_snapshot)) : null;

  // Fall back to deriving subtotal/VAT from the stored gross total when no pricing_snapshot was saved
  const grossPence = quote.total_amount_pence || 0;
  const subtotalPence = pricingSnapshot?.subtotal_pence ?? Math.round(grossPence / 1.2);
  const vatPence = pricingSnapshot?.vat_pence ?? (grossPence - subtotalPence);

  // Some legacy quotes have no linked service record and/or bad seed data
  // (property_type holding a raw category id, postcode literally "TBD").
  // Prefer the more descriptive fields and hide placeholder-looking values.
  const displayServiceName = (quote.service_name && quote.service_name !== 'Service Requested')
    ? quote.service_name
    : (quote.service_type || quote.category_name || 'Service');
  const displayPropertyType = (quote.property_type && !/^\d+$/.test(quote.property_type))
    ? quote.property_type
    : 'Not specified';
  const displayLocation = (quote.postcode && quote.postcode.toUpperCase() !== 'TBD')
    ? quote.postcode
    : 'Not specified';

  // Safe date formatting functions
  const formatDateSafe = (dateStr: any) => {
    if (!dateStr) return 'Not specified';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return 'Not specified';
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch (e) {
      return 'Not specified';
    }
  };

  const formatTimeSafe = (timeStr: any) => {
    if (!timeStr) return 'Not specified';
    // Handle both HH:MM and HH:MM:SS formats
    return timeStr.toString().substring(0, 5);
  };

  return (
    <>
      <Helmet>
        <title>{quote ? `Quote #${quote.id} Details | Virewire` : 'Quote Details | Virewire'}</title>
        <meta name="description" content={quote ? `View details for quote #${quote.id}` : 'Quote details'} />
      </Helmet>

      <DashboardLayout>
        <div className="w-full">{/* Header Section */}
          <div className="mb-8 pb-8 border-b border-gray-200">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mb-6">
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-600 uppercase tracking-wider mb-2">#ORD-{quote.id}</p>
                <h1 className="text-4xl font-bold text-gray-900 mb-3">{displayServiceName}</h1>
                <div className="flex items-center gap-2 text-gray-600 mb-2">
                  <MapPin className="h-4 w-4" />
                  <p>{displayLocation}</p>
                </div>
                <div className="flex items-center gap-2 text-gray-600 text-sm">
                  <span className="font-medium">Property Type:</span>
                  <span className="text-gray-700">{displayPropertyType}</span>
                </div>
              </div>
              
              <div className="flex flex-col items-end gap-4">
                <Badge className={`text-lg px-4 py-2 font-semibold border ${
                  quote.approval_status?.includes('approved') ? 'bg-green-100 text-green-800 border-green-300' :
                  quote.approval_status?.includes('submitted') ? 'bg-blue-100 text-blue-800 border-blue-300' :
                  'bg-gray-100 text-gray-800 border-gray-300'
                }`}>
                  {quote.approval_status?.replace(/_/g, ' ').toUpperCase() || 'PENDING'}
                </Badge>
                <div className="text-right">
                  <p className="text-sm text-gray-600 mb-1">Amount</p>
                  <p className="text-3xl font-bold text-gray-900">£{amountGBP}</p>
                </div>
              </div>
            </div>

            {/* Status Message */}
            {quote.approval_status === 'approved' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-blue-900">Our team is currently on-site. We are monitoring the progress for you.</p>
                </div>
              </div>
            )}

            {/* Dynamic Price Recalculation Notice - Only for pending quotes, NOT for finalized quotes */}
            {recalculatedPrice && priceDifference && priceDifference.difference > 0 && !isQuoteFinalized && (
              <div className={`p-4 rounded-lg border flex items-start gap-3 mb-6 ${
                priceDifference.isIncrease 
                  ? 'bg-orange-50 border-orange-200' 
                  : 'bg-green-50 border-green-200'
              }`}>
                <AlertCircle className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
                  priceDifference.isIncrease ? 'text-orange-600' : 'text-green-600'
                }`} />
                <div className="flex-1">
                  <p className={`font-semibold ${
                    priceDifference.isIncrease 
                      ? 'text-orange-900' 
                      : 'text-green-900'
                  }`}>
                    Pricing {priceDifference.isIncrease ? 'Updated' : 'Changed'}
                  </p>
                  <p className={`text-sm mt-1 ${
                    priceDifference.isIncrease 
                      ? 'text-orange-800' 
                      : 'text-green-800'
                  }`}>
                    Your original quote: ${amountGBP} | Current price: ${recalculatedPrice.grand_total_gbp}
                    <br/>
                    {priceDifference.isIncrease ? 'Increase' : 'Decrease'}: {priceDifference.formattedDifference} ({priceDifference.percentChange}%)
                  </p>
                  {priceDifference.isIncrease && (
                    <p className="text-xs text-orange-700 mt-2">
                      ℹ️ Pricing rules have been updated. Your final price may differ when you confirm the order.
                    </p>
                  )}
                  {!priceDifference.isIncrease && (
                    <p className="text-xs text-green-700 mt-2">
                      ✓ Great news! Current pricing is more favorable than your original quote.
                    </p>
                  )}
                </div>
                {isRecalculating && (
                  <div className="text-sm text-gray-500">
                    <Loader className="h-4 w-4 animate-spin" />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-gray-200 mb-8">
            <div className="flex gap-8 overflow-x-auto">
              {[
                { id: 'overview', label: 'Overview' },
                { id: 'detail', label: 'Service Detail' },
                { id: 'timeline', label: 'Timeline' },
                { id: 'payment', label: 'Invoice & Payment' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-4 font-medium text-sm border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="grid lg:grid-cols-4 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-3">
              {activeTab === 'overview' && (
                <div className="space-y-8">
                  {/* Scope of Work */}
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <FileText className="h-6 w-6 text-primary" />
                      <h2 className="text-2xl font-bold text-gray-900">Scope of Work</h2>
                    </div>
                    <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mb-6">
                      <p className="text-gray-700 leading-relaxed">
                        {displayServiceName} service for the property at {displayLocation}.
                        This comprehensive service includes all required work to ensure your property is properly maintained and any issues are resolved efficiently.
                      </p>
                    </div>

                    {/* Request Context */}
                    <div className="border-l-4 border-primary bg-white p-6 rounded-lg">
                      <h3 className="text-sm font-bold text-primary uppercase tracking-wider mb-3">REQUEST CONTEXT</h3>
                      <p className="text-sm text-gray-600 italic mb-3">"Details submitted with your service request"</p>
                      <ul className="space-y-2 text-sm text-gray-700">
                        <li>• Property Type: {displayPropertyType}</li>
                        <li>• Service Category: {quote.category_name}</li>
                        <li>• Location: {displayLocation}</li>
                        {quote.preferred_service_date && (
                          <>
                            <li>• Preferred Date: {formatDateSafe(quote.preferred_service_date)}</li>
                            <li>• Preferred Time: {formatTimeSafe(quote.preferred_service_time)}</li>
                          </>
                        )}
                      </ul>
                    </div>
                  </div>

                  {/* Quote Scope & Details */}
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Quote Scope */}
                    <div>
                      <div className="flex items-center gap-2 mb-4">
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                        <h3 className="text-lg font-bold text-gray-900">Quote Scope</h3>
                      </div>
                      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                        <p className="text-sm text-gray-700 mb-3">
                          <span className="font-semibold">Service: </span>{displayServiceName}
                        </p>
                        <p className="text-sm text-gray-700 mb-3">
                          <span className="font-semibold">Category: </span>{quote.category_name}
                        </p>
                        <p className="text-sm text-gray-700 mb-3">
                          <span className="font-semibold">Property: </span>{displayPropertyType}
                        </p>
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold">Location: </span>{displayLocation}
                        </p>
                      </div>
                    </div>

                    {/* Quote Status */}
                    <div>
                      <div className="flex items-center gap-2 mb-4">
                        <AlertCircle className="h-5 w-5 text-blue-600" />
                        <h3 className="text-lg font-bold text-gray-900">Quote Status</h3>
                      </div>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                        <p className="text-sm text-gray-700 mb-3">
                          <span className="font-semibold">Status: </span>
                          <span className="text-blue-700 font-semibold">{quote.approval_status?.replace(/_/g, ' ').toUpperCase()}</span>
                        </p>
                        <p className="text-sm text-gray-700 mb-3">
                          <span className="font-semibold">Created: </span>
                          {formatDateSafe(quote.created_at)}
                        </p>
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold">Valid Until: </span>
                          {formatDateSafe(new Date(new Date(quote.created_at).getTime() + 30 * 24 * 60 * 60 * 1000).toISOString())}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Service Details Note */}
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Service Details</h3>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                      <p className="text-sm text-gray-700">
                        This quote is valid for 30 days from the date of issue. For questions about inclusions or exclusions in this quote, please contact our team directly.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'detail' && (
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Service Details</h2>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <p className="text-sm text-gray-600 font-semibold uppercase mb-2">Service</p>
                      <p className="text-lg font-semibold text-gray-900">{displayServiceName}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 font-semibold uppercase mb-2">Category</p>
                      <p className="text-lg font-semibold text-gray-900">{quote.category_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 font-semibold uppercase mb-2">Property Type</p>
                      <p className="text-lg font-semibold text-gray-900">{displayPropertyType}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 font-semibold uppercase mb-2">Location</p>
                      <p className="text-lg font-semibold text-gray-900">{displayLocation}</p>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'timeline' && (
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Progress Timeline</h2>
                  <WorkProgressTimeline
                    status={timelineStatus}
                    submittedAt={quote.created_at}
                    quotedAt={(quote as any).quoted_at || (quote as any).admin_approved_at}
                    paidAt={(quote as any).paid_at || (quote as any).payment_confirmed_at}
                    providerAssignedAt={(quote as any).provider_assigned_at}
                    bookingConfirmedAt={(quote as any).booking_confirmed_at}
                    startedAt={(quote as any).started_at}
                    completedAt={(quote as any).completed_at}
                    scheduledDate={quote.preferred_service_date}
                    scheduledTime={quote.preferred_service_time}
                    providerName={(quote as any).provider_name}
                  />
                </div>
              )}

              {activeTab === 'payment' && (
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Invoice & Payment</h2>
                  <div className="space-y-6">
                    <div className="space-y-4">
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-gray-700">Subtotal</span>
                        <span className="font-semibold">£{(subtotalPence / 100).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-gray-700">VAT (20%)</span>
                        <span className="font-semibold">£{(vatPence / 100).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between py-3 bg-gray-50 px-4 rounded font-bold">
                        <span>Total</span>
                        <span className="text-primary">£{amountGBP}</span>
                      </div>
                    </div>

                    {/* Payment Status */}
                    <div className={`p-4 rounded-lg border ${
                      quote.payment_status === 'completed' || quote.payment_status?.toLowerCase() === 'paid'
                        ? 'bg-green-50 border-green-200'
                        : 'bg-yellow-50 border-yellow-200'
                    }`}>
                      <p className="text-xs text-gray-600 font-semibold uppercase mb-2">Payment Status</p>
                      <p className={`text-lg font-bold ${
                        quote.payment_status === 'completed' || quote.payment_status?.toLowerCase() === 'paid'
                          ? 'text-green-700'
                          : 'text-yellow-700'
                      }`}>
                        {quote.payment_status === 'completed' || quote.payment_status?.toLowerCase() === 'paid' ? '✓ Paid' : 'Pending'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {quote.payment_status === 'completed' || quote.payment_status?.toLowerCase() === 'paid'
                          ? 'Payment received and confirmed'
                          : 'Awaiting payment confirmation'}
                      </p>
                    </div>

                    {/* Pay Now Button */}
                    {quote?.approval_status && quote.payment_status !== 'completed' && quote.payment_status?.toLowerCase() !== 'paid' && (
                      <Button onClick={handlePayNow} className="w-full bg-emerald-700 hover:bg-emerald-800 h-12 font-semibold text-base">
                        <CreditCard className="h-5 w-5 mr-2" />
                        Pay Now - £{amountGBP}
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="space-y-6 lg:sticky lg:top-6 lg:will-change-transform">
                {/* Financial Breakdown Card */}
                <Card className="shadow-lg border-0 bg-white">
                <CardHeader className="bg-gradient-to-r from-emerald-700 to-emerald-600 text-white rounded-t-lg">
                  <CardTitle className="text-lg">Financial Breakdown</CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  {/* Display itemized breakdown from pricing snapshot if available */}
                  {pricingSnapshot?.items && Array.isArray(pricingSnapshot.items) && pricingSnapshot.items.length > 0 ? (
                    pricingSnapshot.items.map((item: any, idx: number) => (
                      <div key={idx} className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">{item.description || `Item ${idx + 1}`}</span>
                        <span className="font-semibold text-gray-900">
                          {item.total_pence ? `£${(item.total_pence / 100).toFixed(2)}` : (item.multiplier_applied ? `${((item.multiplier_applied - 1) * 100).toFixed(0)}%` : '—')}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-600">{displayServiceName}</span>
                      <span className="font-semibold text-gray-900">£{(subtotalPence / 100).toFixed(2)}</span>
                    </div>
                  )}

                  <div className="bg-emerald-50 p-4 rounded-lg">
                    <p className="text-xs text-gray-600 font-semibold uppercase mb-2">Total Quoted Value</p>
                    <p className="text-2xl font-bold text-emerald-700">£{amountGBP}</p>
                  </div>
                  <div className={`p-4 rounded-lg border ${
                    quote.payment_status === 'completed' || quote.payment_status?.toLowerCase() === 'paid'
                      ? 'bg-green-50 border-green-200'
                      : 'bg-yellow-50 border-yellow-200'
                  }`}>
                    <p className="text-xs text-gray-600 font-semibold uppercase mb-2">Payment Status</p>
                    <p className={`text-lg font-bold ${
                      quote.payment_status === 'completed' || quote.payment_status?.toLowerCase() === 'paid'
                        ? 'text-green-700'
                        : 'text-yellow-700'
                    }`}>
                      {quote.payment_status === 'completed' || quote.payment_status?.toLowerCase() === 'paid' ? '✓ Paid' : 'Pending'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {quote.payment_status === 'completed' || quote.payment_status?.toLowerCase() === 'paid'
                        ? 'Payment received and confirmed'
                        : 'Awaiting payment confirmation'}
                    </p>
                  </div>

                  {/* Action Button */}
                  {quote?.approval_status && quote.payment_status !== 'completed' && quote.payment_status?.toLowerCase() !== 'paid' && (
                    <Button onClick={handlePayNow} className="w-full bg-emerald-700 hover:bg-emerald-800 h-11 font-semibold text-base">
                      <CreditCard className="h-4 w-4 mr-2" />
                      Pay Now - £{amountGBP}
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Project Team Card */}
              <Card className="shadow-lg border-0">
                <CardHeader className="bg-white border-b border-gray-200">
                  <CardTitle className="text-lg text-gray-900">Your Support Team</CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
                    <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold">
                      V
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Virewire Concierge Team</p>
                      <p className="text-xs text-gray-600">A provider will be assigned once your quote is approved</p>
                    </div>
                  </div>
                  <Button variant="outline" className="w-full h-9 text-sm justify-center" onClick={() => navigate('/dashboard/support')}>
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Message Team
                  </Button>
                </CardContent>
              </Card>

              {/* Need a Change Card */}
              <Card className="shadow-lg border-0 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
                <CardContent className="p-6">
                  <h3 className="font-bold text-lg mb-2">Need a change?</h3>
                  <p className="text-sm text-gray-300 mb-4">Request scope adjustments or add-on services directly through our managed portal.</p>
                  <Button onClick={() => setRescheduleModalOpen(true)} className="w-full bg-white text-gray-900 hover:bg-gray-100 h-9 text-sm font-semibold">
                    REQUEST CHANGE ORDER
                  </Button>
                </CardContent>
              </Card>
            </div>
            </div>
          </div>

          {/* Delete Confirmation Dialog */}
          {deleteConfirmOpen && (
            <>
              <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setDeleteConfirmOpen(false)} />
              <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
                <Card className="max-w-sm w-full shadow-xl">
                  <CardHeader className="bg-red-50 border-b border-red-200">
                    <CardTitle className="text-red-600 text-lg flex items-center gap-2">
                      <AlertCircle className="h-5 w-5" />
                      Delete Quote?
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6 space-y-4">
                    <p className="text-sm text-gray-700 leading-relaxed">
                      Are you sure you want to delete this quote? This action cannot be undone.
                    </p>
                    <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                      <p className="text-sm text-red-900 font-semibold">Quote #{quote.id} - {displayServiceName}</p>
                    </div>
                    <div className="flex gap-3 pt-4">
                      <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)} disabled={isDeleting} className="flex-1 h-9">
                        Cancel
                      </Button>
                      <Button variant="destructive" onClick={handleDeleteQuote} disabled={isDeleting} className="flex-1 h-9">
                        {isDeleting ? 'Deleting...' : 'Delete'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}

          {/* Reschedule Modal */}
          {quote && (
            <RescheduleModal
              isOpen={rescheduleModalOpen}
              quoteId={quote.id}
              currentDate={quote.preferred_service_date}
              currentTime={quote.preferred_service_time}
              onClose={() => setRescheduleModalOpen(false)}
              onSuccess={() => {
                setRescheduleModalOpen(false);
                window.location.reload();
              }}
            />
          )}
        </div>
      </DashboardLayout>
    </>
  );
};

export default QuoteDetailsPage;
